'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useStore } from '@/context/store-context';
import { apiGet } from '@/lib/api';

export interface GeneralSettingsSlice {
  fraction?: number;
  quantity_fraction?: number;
  default_currency?: string;
  [key: string]: unknown;
}

interface SettingsContextValue {
  fraction: number;
  quantity_fraction: number;
  /** Format a monetary/amount value using the configured fraction (decimal places). */
  formatAmount: (value: number | string) => string;
  /** Format a quantity value using the configured quantity fraction (decimal places). */
  formatQuantity: (value: number | string) => string;
  loading: boolean;
  /** Sidebar / layout preferences from general settings */
  dark_topbar: boolean;
  dark_sidebar: boolean;
  sidebar_dropdown: boolean;
  sidebar_scroll_to_view: boolean;
}

const DEFAULTS = {
  fraction: 2,
  quantity_fraction: 2,
  dark_topbar: false,
  dark_sidebar: false,
  sidebar_dropdown: false,
  sidebar_scroll_to_view: false,
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

function toNum(v: number | string): number {
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  const n = typeof v === 'string' ? parseFloat(v) : Number(v);
  return Number.isNaN(n) ? 0 : n;
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const { currentStore } = useStore();
  const [data, setData] = useState<GeneralSettingsSlice>({});
  const [loading, setLoading] = useState(true);
  /** Tracks which storeId we last started loading for; used to ignore stale responses (race when store loads after mount). */
  const loadStoreIdRef = useRef<string | null>(null);

  const fraction = typeof data.fraction === 'number' && data.fraction >= 0 && data.fraction <= 4
    ? data.fraction
    : DEFAULTS.fraction;
  const quantity_fraction = typeof data.quantity_fraction === 'number' && data.quantity_fraction >= 0 && data.quantity_fraction <= 4
    ? data.quantity_fraction
    : DEFAULTS.quantity_fraction;

  // Accept boolean or truthy values from API (e.g. true, "true", 1) so toggles work reliably
  const dark_topbar = !!data.dark_topbar;
  const dark_sidebar = !!data.dark_sidebar;
  const sidebar_dropdown = !!data.sidebar_dropdown;
  const sidebar_scroll_to_view = !!data.sidebar_scroll_to_view;

  const load = useCallback(async () => {
    const requestedStoreId = currentStore?.id ?? null;
    loadStoreIdRef.current = requestedStoreId;
    setLoading(true);
    const query = requestedStoreId ? `?storeId=${encodeURIComponent(requestedStoreId)}` : '';
    const res = await apiGet<GeneralSettingsSlice>('/settings/general' + query);
    // Only apply if this response is still current (no newer load started for a different store)
    if (loadStoreIdRef.current === requestedStoreId) {
      if (res.data && typeof res.data === 'object') setData(res.data as GeneralSettingsSlice);
      else setData({});
    }
    setLoading(false);
  }, [currentStore?.id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ data?: Record<string, unknown> }>)?.detail;
      if (detail?.data && typeof detail.data === 'object') {
        setData(detail.data as GeneralSettingsSlice);
      } else {
        load();
      }
    };
    window.addEventListener('settings-saved', handler);
    return () => window.removeEventListener('settings-saved', handler);
  }, [load]);

  const formatAmount = useCallback((value: number | string) => {
    return toNum(value).toFixed(fraction);
  }, [fraction]);

  const formatQuantity = useCallback((value: number | string) => {
    return toNum(value).toFixed(quantity_fraction);
  }, [quantity_fraction]);

  const value: SettingsContextValue = {
    fraction,
    quantity_fraction,
    formatAmount,
    formatQuantity,
    loading,
    dark_topbar,
    dark_sidebar,
    sidebar_dropdown,
    sidebar_scroll_to_view,
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) {
    const formatAmount = (v: number | string) => toNum(v).toFixed(DEFAULTS.fraction);
    const formatQuantity = (v: number | string) => toNum(v).toFixed(DEFAULTS.quantity_fraction);
    return {
      fraction: DEFAULTS.fraction,
      quantity_fraction: DEFAULTS.quantity_fraction,
      formatAmount,
      formatQuantity,
      loading: false,
      dark_topbar: DEFAULTS.dark_topbar,
      dark_sidebar: DEFAULTS.dark_sidebar,
      sidebar_dropdown: DEFAULTS.sidebar_dropdown,
      sidebar_scroll_to_view: DEFAULTS.sidebar_scroll_to_view,
    } as SettingsContextValue;
  }
  return ctx;
}
